'use client';

import { useState } from 'react';
import ServiceAreaCard from './ServiceAreaCard';
import InteractiveMap from './InteractiveMap';
import WeatherIntegration from './WeatherIntegration';
import CommunityTestimonials from './CommunityTestimonials';
import Icon from '@/components/ui/AppIcon';

interface ServiceArea {
  id: number;
  name: string;
  slug: string;
  image: string;
  alt: string;
  description: string;
  neighborhoods: string[];
  specialties: string[];
  responseTime: string;
  testimonialCount: number;
  region: number;
}

interface MapRegion {
  id: number;
  name: string;
  cities: string[];
  coordinates: string;
}

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  service: string;
  date: string;
}

interface ServiceAreasInteractiveProps {
  serviceAreas: ServiceArea[];
  mapRegions: MapRegion[];
  testimonials: Testimonial[];
}

export default function ServiceAreasInteractive({
  serviceAreas,
  mapRegions,
  testimonials
}: ServiceAreasInteractiveProps) {
  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'responseTime'>('name');

  const handleRegionSelect = (regionId: number) => {
    setSelectedRegion(selectedRegion === regionId ? null : regionId);
  };

  const filteredAreas = serviceAreas.filter((area) => {
    const matchesSearch = area.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         area.neighborhoods.some(n => n.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesRegion = selectedRegion === null || area.region === selectedRegion;
    return matchesSearch && matchesRegion;
  });

  const sortedAreas = [...filteredAreas].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    return a.responseTime.localeCompare(b.responseTime);
  });

  return (
    <div className="space-y-8">
      {/* Search and Filter Section */}
      <div className="bg-card rounded-lg shadow-brand p-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search by city or neighborhood..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="text-sm font-body font-medium text-foreground whitespace-nowrap">
              Sort by:
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'responseTime')}
              className="flex-1 px-4 py-2 border border-border rounded-md font-body text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="name">City Name</option>
              <option value="responseTime">Response Time</option>
            </select>
          </div>
        </div>

        {selectedRegion && (
          <div className="mt-4 flex items-center justify-between bg-accent/10 border border-accent rounded-md px-4 py-2">
            <div className="flex items-center space-x-2">
              <Icon name="FunnelIcon" size={16} className="text-accent" />
              <span className="text-sm font-body text-foreground">
                Filtered by: {mapRegions.find(r => r.id === selectedRegion)?.name}
              </span>
            </div>
            <button
              onClick={() => setSelectedRegion(null)}
              className="text-sm font-body font-medium text-accent hover:text-accent/80 transition-brand"
            >
              Clear Filter
            </button>
          </div>
        )}
      </div>

      {/* Interactive Map */}
      <InteractiveMap regions={mapRegions} onRegionSelect={handleRegionSelect} />

      {/* Weather Integration */}
      <WeatherIntegration />

      {/* Service Areas Grid */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-headline font-bold text-2xl text-primary">
            {selectedRegion
              ? `${mapRegions.find(r => r.id === selectedRegion)?.name} Service Areas`
              : 'All Service Areas'}
          </h2>
          <span className="text-sm text-muted-foreground font-body">
            {sortedAreas.length} {sortedAreas.length === 1 ? 'area' : 'areas'} found
          </span>
        </div>

        {sortedAreas.length === 0 ? (
          <div className="bg-card rounded-lg shadow-brand p-12 text-center">
            <Icon name="MapPinIcon" size={48} className="text-muted mx-auto mb-4" />
            <h3 className="font-headline font-bold text-xl text-primary mb-2">
              No Areas Found
            </h3>
            <p className="text-muted-foreground font-body mb-4">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedRegion(null);
              }}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-md font-body font-medium hover:bg-primary/90 transition-brand"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedAreas.map((area) => (
              <ServiceAreaCard key={area.id} area={area} />
            ))}
          </div>
        )}
      </div>

      {/* Community Testimonials */}
      <CommunityTestimonials
        testimonials={testimonials}
        areaName="Phoenix Metro"
      />
    </div>
  );
}